'use client'

import { motion } from 'framer-motion'
import { 
  Footer,
  Navigation
} from '@/ui/design-system'
import { PageLayout } from '@/ui/layout'
import { Logo } from '@/ui/components'
import { PageMeta } from '@/components/PageMeta'

export default function PrivacyPolicy() {
  // Navigation configuration
  const navigationItems = [
    {
      title: "Home",
      href: "/",
    },
  ]

  const navigationActions = {
    primary: { label: "Get started", href: "/signup" },
    secondary: { label: "Sign in", href: "/login" }
  }

  // Footer configuration
  const footerSections = [
    {
      title: "Product",
      links: [
        { label: "About Us", href: "/about" },
        { label: "Sign Up", href: "/signup" },
      ]
    },
    {
      title: "Support",
      links: [
        { label: "Documentation", href: "/docs" },
        { label: "Contact Us", href: "/contact" },
        { label: "Report Issue", href: "/contact" },
      ]
    },
    {
      title: "Legal",
      links: [
        { label: "Terms of Service", href: "/terms" },
        { label: "Privacy Policy", href: "/privacy" },
      ]
    }
  ]

  return (
    <PageLayout showHeader={false}>
      <PageMeta title="Privacy Policy" description="SitRepp.io privacy policy and data practices" />
      <Navigation
        logo={<Logo to="/" size="md" />}
        items={navigationItems}
        actions={navigationActions}
      />

      <div className="min-h-screen bg-earth-50">
        {/* Hero Section */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center max-w-4xl mx-auto mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight">
                Privacy Policy
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-earth-50">
          <div className="max-w-6xl mx-auto">
            <div className="space-y-16">
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">1. Introduction</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Welcome to SitRepp.io ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, mobile application, and services (collectively, the "Service").
                  </p>
                  <p>
                    By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our Service.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">2. Information We Collect</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    We collect several types of information from and about users of our Service:
                  </p>
                  
                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3 mt-6">2.1. Personal Information</h3>
                    <p className="mb-3">
                      When you register for an account, we collect:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Email address</li>
                      <li>Password (encrypted and stored securely)</li>
                      <li>Profile information (bio, location, experience level, trip preferences)</li>
                      <li>Profile and banner images</li>
                      <li>Social media links (optional)</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3 mt-6">2.2. Trip and Location Data</h3>
                    <p className="mb-3">
                      When you create or participate in trips, we collect:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Trip information (name, description, dates, locations)</li>
                      <li>Waypoints and route coordinates (latitude/longitude)</li>
                      <li>Meetup locations and times</li>
                      <li>Route planning data</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3 mt-6">2.3. Vehicle and Gear Information</h3>
                    <p className="mb-3">
                      When you add vehicles or gear to your profile, we collect:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Vehicle details (make, model, year, modifications)</li>
                      <li>Gear inventory and specifications</li>
                      <li>Vehicle and gear photos</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3 mt-6">2.4. User-Generated Content</h3>
                    <p className="mb-3">
                      We collect content you create and share:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Photos and media files uploaded to trips</li>
                      <li>Meal plans and recipes</li>
                      <li>Trip notes and comments</li>
                      <li>Action items and task lists</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3 mt-6">2.5. Automatically Collected Information</h3>
                    <p className="mb-3">
                      When you use our Service, we automatically collect:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Device information (browser type, operating system)</li>
                      <li>IP address and general location data</li>
                      <li>Usage data (pages visited, features used, time spent)</li>
                      <li>Authentication tokens (stored securely in your browser)</li>
                    </ul>
                  </div>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">3. How We Use Your Information</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    We use the information we collect for the following purposes:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>To provide, maintain, and improve our Service</li>
                    <li>To create and manage your account</li>
                    <li>To enable trip planning, coordination, and collaboration features</li>
                    <li>To process and store your trip data, routes, and waypoints</li>
                    <li>To facilitate communication between trip members</li>
                    <li>To provide location-based services and mapping features</li>
                    <li>To send you service-related notifications and updates</li>
                    <li>To respond to your inquiries and provide customer support</li>
                    <li>To detect, prevent, and address technical issues and security threats</li>
                    <li>To comply with legal obligations and enforce our Terms of Service</li>
                  </ul>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">4. Third-Party Services</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    We use third-party services to operate and improve our Service. These services may collect, process, and store your information:
                  </p>
                  
                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3 mt-6">4.1. Supabase</h3>
                    <p className="mb-3">
                      We use Supabase for database storage, user authentication, and file storage. Your data is stored securely in Supabase's infrastructure. Please review Supabase's Privacy Policy: <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://supabase.com/privacy</a>
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3 mt-6">4.2. Mapbox</h3>
                    <p className="mb-3">
                      We use Mapbox for mapping, geocoding, and route planning services. When you use mapping features, Mapbox may collect location data and usage information. Please review Mapbox's Privacy Policy: <a href="https://www.mapbox.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.mapbox.com/legal/privacy</a>
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3 mt-6">4.3. OpenWeather</h3>
                    <p className="mb-3">
                      We use OpenWeather API to provide weather information for your trips. Location data may be shared with OpenWeather to retrieve relevant weather forecasts. Please review OpenWeather's Privacy Policy: <a href="https://openweather.co.uk/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://openweather.co.uk/privacy-policy</a>
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3 mt-6">4.4. OpenAI</h3>
                    <p className="mb-3">
                      We use OpenAI's API to power our Ranger AI assistant feature. When you interact with Ranger AI, your queries and trip context may be processed by OpenAI. Please review OpenAI's Privacy Policy: <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://openai.com/policies/privacy-policy</a>
                    </p>
                  </div>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">5. Data Storage and Security</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    We implement appropriate technical and organizational security measures to protect your personal information:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>All data is encrypted in transit using HTTPS/TLS</li>
                    <li>Passwords are hashed and never stored in plain text</li>
                    <li>Authentication tokens are stored securely in your browser's localStorage</li>
                    <li>Database access is protected by Row Level Security (RLS) policies</li>
                    <li>File uploads are stored in secure, access-controlled storage buckets</li>
                    <li>Regular security audits and monitoring</li>
                  </ul>
                  <p>
                    However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">6. Cookies and Local Storage</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    We use browser localStorage and sessionStorage to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Store authentication tokens for session management</li>
                    <li>Remember your preferences and settings</li>
                    <li>Maintain your session state</li>
                    <li>Store route persistence information</li>
                  </ul>
                  <p>
                    You can control these storage mechanisms through your browser settings. Note that disabling localStorage may affect the functionality of our Service.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">7. Data Sharing and Disclosure</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    We do not sell your personal information. We may share your information in the following circumstances:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong className="text-foreground">With Trip Members:</strong> When you join a trip, your profile information and contributions are visible to other trip members</li>
                    <li><strong className="text-foreground">Public Trips:</strong> If you create or join a public trip, certain trip information may be visible to all users</li>
                    <li><strong className="text-foreground">Service Providers:</strong> We share data with third-party service providers (Supabase, Mapbox, etc.) necessary to operate our Service</li>
                    <li><strong className="text-foreground">Legal Requirements:</strong> We may disclose information if required by law or to protect our rights and safety</li>
                    <li><strong className="text-foreground">Business Transfers:</strong> In the event of a merger, acquisition, or sale, your information may be transferred</li>
                  </ul>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">8. Your Rights and Choices</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Depending on your location, you may have certain rights regarding your personal information:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong className="text-foreground">Access:</strong> You can access and review your personal information through your account settings</li>
                    <li><strong className="text-foreground">Correction:</strong> You can update or correct your information at any time</li>
                    <li><strong className="text-foreground">Deletion:</strong> You can delete your account and request deletion of your data</li>
                    <li><strong className="text-foreground">Data Portability:</strong> You can export your trip data and personal information</li>
                    <li><strong className="text-foreground">Opt-Out:</strong> You can opt out of non-essential communications</li>
                    <li><strong className="text-foreground">California Residents:</strong> You have additional rights under the California Consumer Privacy Act (CCPA)</li>
                    <li><strong className="text-foreground">EU Residents:</strong> You have additional rights under the General Data Protection Regulation (GDPR)</li>
                  </ul>
                  <p>
                    To exercise these rights, please contact us at <a href="mailto:support@sitrepp.io" className="text-primary hover:underline">support@sitrepp.io</a>
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">9. Data Retention</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    We retain your personal information for as long as necessary to provide our Service and fulfill the purposes outlined in this Privacy Policy. Specifically:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Account information is retained while your account is active</li>
                    <li>Trip data is retained until you delete it or your account is deleted</li>
                    <li>We may retain certain information for legal, regulatory, or business purposes</li>
                    <li>When you delete your account, we will delete or anonymize your personal information within 30 days</li>
                  </ul>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">10. Children's Privacy</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Our Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">11. International Data Transfers</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using our Service, you consent to the transfer of your information to these countries. We take appropriate safeguards to ensure your information is protected in accordance with this Privacy Policy.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">12. Changes to This Privacy Policy</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    We may update this Privacy Policy from time to time. We will notify you of any material changes by:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Posting the new Privacy Policy on this page</li>
                    <li>Updating the "Last updated" date at the top of this Privacy Policy</li>
                    <li>Sending you an email notification (if you have an account)</li>
                    <li>Displaying a notice on our Service</li>
                  </ul>
                  <p>
                    Your continued use of our Service after any changes constitutes acceptance of the updated Privacy Policy.
                  </p>
                </div>
              </motion.section>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">13. Contact Us</h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                  </p>
                  <p>
                    <strong className="text-foreground">Email:</strong> <a href="mailto:support@sitrepp.io" className="text-primary hover:underline">support@sitrepp.io</a>
                  </p>
                  <p>
                    <strong className="text-foreground">Website:</strong> <a href="/contact" className="text-primary hover:underline">sitrepp.io/contact</a>
                  </p>
                </div>
              </motion.section>
            </div>
          </div>
        </section>
      </div>

      <Footer
        sections={footerSections}
        description="A collaborative workspace for overlanding crews. Coordinate trips, manage gear, plan meals, and share memories—all in one place."
      />
    </PageLayout>
  )
}

